import React from "react";
import GlassCard from "../ui/GlassCard";
import Button from "../ui/Button";

interface BankCardProps {
  bank: any;
  section?: any;
  questions: any[];
  questionTypes: any[];
  isExpanded: boolean;
  onToggleExpand: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onAddQuestion: () => void;
  onBulkUpload: () => void;
  onEditQuestion: (question: any) => void;
  onDeleteQuestion: (question: any) => void;
}

const BankCard: React.FC<BankCardProps> = ({
  bank,
  section,
  questions,
  questionTypes,
  isExpanded,
  onToggleExpand,
  onEdit,
  onDelete,
  onAddQuestion,
  onBulkUpload,
  onEditQuestion,
  onDeleteQuestion
}) => {
  const getDifficultyColor = (level: string) => {
    switch (level) {
      case 'easy': return 'bg-green-100 text-green-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'hard': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getDifficultyLabel = (level: string) => {
    switch (level) {
      case 'easy': return 'Easy';
      case 'medium': return 'Medium';
      case 'hard': return 'Hard';
      default: return level;
    }
  };

  const getQuestionTypeName = (typeId: string) => {
    const type = questionTypes.find(qt => qt.id === typeId);
    return type?.name || typeId;
  };

  const questionStats = {
    total: questions.length,
    byDifficulty: {
      easy: questions.filter(q => q.difficultyLevel === 'easy').length,
      medium: questions.filter(q => q.difficultyLevel === 'medium').length,
      hard: questions.filter(q => q.difficultyLevel === 'hard').length
    },
    byType: questions.reduce((acc, q) => {
      const typeName = getQuestionTypeName(q.questionTypeId);
      acc[typeName] = (acc[typeName] || 0) + 1;
      return acc;
    }, {} as Record<string, number>)
  };

  return (
    <GlassCard className="overflow-hidden">
      <div className="p-5 cursor-pointer" onClick={onToggleExpand}>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="text-lg font-semibold text-gray-800">{bank.name}</h3>
              {bank.status === 'active' ? (
                <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">Active</span>
              ) : (
                <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700">Inactive</span>
              )}
            </div>
            {bank.description && (
              <p className="text-sm text-gray-600 mb-2">{bank.description}</p>
            )}
            {section && (
              <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                <span>📚 {section.name}</span>
                <span>📋 {section.sectionType}</span>
                <span>❓ {questions.length} questions</span>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={(e) => { e.stopPropagation(); onEdit(); }}
            >
              Edit
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
            >
              Delete
            </Button>
            <button className="text-gray-400 hover:text-gray-600">
              {isExpanded ? '▼' : '▶'}
            </button>
          </div>
        </div>

        <div className="mt-3 grid grid-cols-4 gap-3">
          <div className="text-center p-2 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500">Total</p>
            <p className="text-lg font-bold text-gray-800">{questionStats.total}</p>
          </div>
          <div className="text-center p-2 bg-green-50 rounded-lg">
            <p className="text-xs text-green-600">Easy</p>
            <p className="text-lg font-bold text-green-700">{questionStats.byDifficulty.easy}</p>
          </div>
          <div className="text-center p-2 bg-yellow-50 rounded-lg">
            <p className="text-xs text-yellow-600">Medium</p>
            <p className="text-lg font-bold text-yellow-700">{questionStats.byDifficulty.medium}</p>
          </div>
          <div className="text-center p-2 bg-red-50 rounded-lg">
            <p className="text-xs text-red-600">Hard</p>
            <p className="text-lg font-bold text-red-700">{questionStats.byDifficulty.hard}</p>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="border-t border-gray-200 p-5">
          <div className="flex justify-between items-center mb-4">
            <h4 className="font-medium text-gray-800">Questions in this Bank</h4>
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" onClick={onBulkUpload}>
                📤 Bulk Upload
              </Button>
              <Button variant="primary" size="sm" onClick={onAddQuestion}>
                + Add Question
              </Button>
            </div>
          </div>

          {questions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No questions in this bank yet.</p>
              <div className="flex gap-2 justify-center mt-2">
                <Button variant="secondary" size="sm" onClick={onBulkUpload}>
                  Bulk Upload Questions
                </Button>
                <Button variant="primary" size="sm" onClick={onAddQuestion}>
                  Add Single Question
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {questions.map(question => {
                const questionType = questionTypes.find(qt => qt.id === question.questionTypeId);
                return (
                  <div key={question.id} className="border rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-sm font-medium text-gray-500">#{question.id.slice(-6)}</span>
                          {questionType && (
                            <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700">
                              {questionType.name}
                            </span>
                          )}
                          <span className={`px-2 py-1 text-xs rounded-full ${getDifficultyColor(question.difficultyLevel)}`}>
                            {getDifficultyLabel(question.difficultyLevel)}
                          </span>
                          {question.marks && (
                            <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-700">
                              {question.marks} marks
                            </span>
                          )}
                        </div>
                        <p className="text-gray-800 mb-2">{question.text}</p>
                        <div className="text-sm text-gray-500">
                          <span>✓ Correct: {question.correctAnswer}</span>
                          {question.timeLimit && question.timeLimit > 0 && (
                            <span className="ml-3">⏱ {question.timeLimit}s</span>
                          )}
                        </div>
                        {question.explanation && (
                          <details className="mt-2">
                            <summary className="text-sm text-indigo-600 cursor-pointer">View Explanation</summary>
                            <p className="text-sm text-gray-600 mt-1">{question.explanation}</p>
                          </details>
                        )}
                      </div>
                      <div className="flex gap-2 ml-4">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => onEditQuestion(question)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => onDeleteQuestion(question)}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </GlassCard>
  );
};

export default BankCard;